<script lang="ts">
  import { slide } from 'svelte/transition';
  import clsx from 'clsx';
  import type { Laminate } from '../types';
  import {
    LaminateType,
    MaterialName,
    StickerPreviewAnimationMode,
    type SpriteSheetData,
    type SheetPos,
    type StickerPreviewProps,
    MaskFillColor,
    MaskSource,
    WhiteLayerMaskMode,
  } from '../types';

  import { generateUniqueId } from '../svg-utils';
  import StickerPreviewLayers from './StickerPreviewLayers.svelte';

  import { type FpsHandler, fpsCounter } from './PreviewTools/fpsCounter.svelte';
  import generateMasks from './PreviewTools/mask-generator';
  import StickerPreviewRulers from './StickerPreviewRulers.svelte';
  const fpsCount: FpsHandler = fpsCounter();
  const shadowOffset = 30;

  let {
    baseProps = $bindable(),
    settings = $bindable(),
    staticUrl,
    isLoading,
    visible = $bindable(false),
    className,
    wiggle,
    productItemDimensions,
    maskSource = MaskSource.COMBINED,
  }: StickerPreviewProps = $props();

  const uniqueId = generateUniqueId();
  const shinyLaminates = [
    LaminateType.Glossy,
    LaminateType.GlossyThin,
    LaminateType.GlossyThinNoUV,
  ];

  let svgContainer: HTMLDivElement;
  let svgCanvas = $state<SVGSVGElement>();
  let laminateImage: SVGImageElement;
  let printImage: SVGImageElement;
  let glitterMaskSprite: SVGGElement;
  let whiteLayerGroup = $state<SVGGElement>();

  let animationRunning = $state(false);
  let abortAnimation = $state(false);

  export function getSVGString(): string | undefined {
    if (!svgCanvas) {
      return undefined;
    }
    const parser = new DOMParser();
    let doc = parser.parseFromString(svgCanvas?.outerHTML, 'image/svg+xml');

    const serializer = new XMLSerializer();
    return serializer.serializeToString(doc);
  }

  function getFillForMaterial(material: MaterialName): string {
    const fills: Record<MaterialName, string> = {
      [MaterialName.Brushed]: '#c0c0c0',
      [MaterialName.Reflective]: '#c0c0c0',
      [MaterialName.Mirror]: '#fefefe',
      [MaterialName.SilverThin]: '#fefefe',
      [MaterialName.Prismatic]: '#d7d7d7',
      [MaterialName.Holographic]: '#ffffff',
      [MaterialName.Glitter]: '#aaaaaa',
      [MaterialName.Pixie]: '#aaaaaa',
      [MaterialName.Gitd]: '#fdffe0',
      [MaterialName.KraftThin]: '#97774a',
      [MaterialName.Recycled]: '#97774a',
      [MaterialName.Vinyl]: '#ffffff',
      [MaterialName.Metal]: '#ffffff',
      [MaterialName.Clear]: '#ffffff38',
      [MaterialName.WhiteRemovable]: '#ffffff',
      [MaterialName.WhiteHiTack]: '#ffffff',
      [MaterialName.WhiteThin]: '#ffffff',
      [MaterialName.WhiteWall]: '#ffffff',
      [MaterialName.WhiteSturdy]: '#ffffff',
      [MaterialName.ClearThin]: '#ffffff38',
      [MaterialName.SatinMatte]: '#ffffff',
    };

    return fills[material];
  }

  let metallicOpacity: number = $derived.by(() => {
    if (!baseProps.material.metallicOpacity) {
      return baseProps.material.isMetal ? 0.3 : 0;
    }

    return typeof baseProps.material.metallicOpacity === 'function'
      ? baseProps.material.metallicOpacity(baseProps.laminate as string)
      : baseProps.material.metallicOpacity;
  });

  let isShineLaminate = $derived(shinyLaminates.includes(baseProps.laminate as Laminate));

  const sizeClasses = $derived.by(() => {
    if (settings.fullSize) {
      return '';
    } else if (settings.lightweight) {
      return 'lightweight-optimizations';
    }
    return 'max-w-[70%] max-width-70 max-h-[70%] max-height-70';
  });

  let frameIndexX = $state(0);
  let frameIndexY = $state(0);

  type MousePosition = {
    x: number;
    y: number;
  };
  let targetMousePosition: MousePosition = {
    x: 0,
    y: 0,
  };
  let currentMousePosition: MousePosition = {
    x: 0,
    y: 0,
  };

  let glitterSheetData: SpriteSheetData = $state(getGlitterSheetData('large'));
  $effect(() => {
    glitterSheetData = getGlitterSheetData('large');
  });
  // Browser detection for performance optimizations
  const isChrome = navigator.userAgent.indexOf('Chrome') > -1;
  // If not in chrome or lightweight set smaller pattern size
  const recycledPatternSize = !isChrome || settings.lightweight ? 500 : 1000;

  function getGlitterPatternScale(type: string): number {
    if (baseProps.material.type === MaterialName.Pixie) {
      return type === 'small' ? settings.patternScale * 0.4 : settings.patternScale * 0.2;
    }

    return type === 'small' ? settings.patternScale * 2 : settings.patternScale * 0.6;
  }

  function getGlitterSheetData(type: string): SpriteSheetData {
    const frameWidth: number = type === 'small' ? 540 : 1080;
    const frameHeight: number = type === 'small' ? 540 : 1080;
    const sizeX: number = 4;
    const sizeY: number = 3;

    let indices: SheetPos[] = [];
    for (let y = 0; y < sizeY; y++) {
      for (let x = 0; x < sizeX; x++) {
        indices.push({
          x: frameWidth * x,
          y: frameHeight * y,
        });
      }
    }

    return {
      indices,
      width: frameWidth * sizeX,
      height: frameHeight * sizeY,
      frameWidth,
      frameHeight,
      imgSrc: `/images/web/preview/effects/glitter-masks/${type === 'small' || settings.lightweight ? 'spritesheet_small' : 'spritesheet_large'}.jpg`,
      patternScale: getGlitterPatternScale(type),
    };
  }

  let fullWidth = $derived.by(() => {
    if (baseProps.sticker.pathWidth) {
      /* Value server side (remove?)*/
      return baseProps.sticker.pathWidth;
    } else {
      /* Sheet preview size */
      return baseProps.sticker.width;
    }
  });

  let fullHeight = $derived.by(() => {
    if (baseProps.sticker.pathHeight) {
      /* Value server side (remove?)*/
      return baseProps.sticker.pathHeight;
    } else {
      /* Sheet preview size */
      return baseProps.sticker.height;
    }
  });

  let holographicImageSize = $derived.by(() => {
    /* Lets just keep the holos image sizes constant shall we? */
    const originalWidth = 1000;
    const originalHeight = 698;

    // Define how much larger the image should be compared to the viewbox
    const bufferFactor = 3;

    let scaledWidth = fullWidth * bufferFactor;
    let scaledHeight = fullHeight * bufferFactor;

    // Calculate width & height ratio
    let widthRatio = scaledWidth / originalWidth;
    let heightRatio = scaledHeight / originalHeight;

    // Choose the larger scaling factor to ensure full coverage
    let scale = Math.max(widthRatio, heightRatio);
    return {
      width: originalWidth * scale,
      height: originalHeight * scale,
      scale: scale,
      bufferFactor: bufferFactor,
    };
  });

  let laminateAndMetallicImageSize = $derived.by(() => {
    const originalWidth = 1090;
    const originalHeight = 1636;
    const bufferFactor = 5;

    let scaledWidth = fullWidth * bufferFactor;
    let scaledHeight = fullHeight * bufferFactor;

    let widthRatio = scaledWidth / originalWidth;
    let heightRatio = scaledHeight / originalHeight;

    let scale = Math.max(widthRatio, heightRatio);

    return {
      width: originalWidth * scale,
      height: originalHeight * scale,
      scale: scale,
      bufferFactor: bufferFactor,
    };
  });

  /* Round these numbers for performance? */
  let posX: number = 0;
  let posY: number = 0;
  let relX: number = $state(0);
  let relY: number = $state(0);
  let angleDeg = 0;

  let whitelayerMaskString = $state('');
  let opacityMaskString = $state('');
  $effect(() => {
    if (baseProps.sticker.originOffset) {
      /* Offset on the original image where the path was generated. */
      printImage?.setAttribute(
        'transform',
        `translate(${baseProps.sticker.originOffset.x.toString()}, ${baseProps.sticker.originOffset?.y.toString()})`,
      );
      if (settings.effectLayerEnabled) {
        whiteLayerGroup?.querySelectorAll('image').forEach((e) => {
          e.setAttribute(
            'transform',
            `translate(${baseProps.sticker.originOffset?.x.toString()}, ${baseProps.sticker.originOffset?.y.toString()})`,
          );
        });
      }
    }
    /* Need parent element for the full size of the container */
  });

  $effect(() => {
    if (animationLoopRef) {
      cancelAnimationFrame(animationLoopRef);
    }
    switch (settings.animationMode) {
      case StickerPreviewAnimationMode.Loop:
        loopAnimation(3000);
        break;
      case StickerPreviewAnimationMode.OnMouseMove:
        svgContainer.addEventListener('mousemove', onMouseMovement);
        return () => svgContainer.removeEventListener('mousemove', onMouseMovement);
      case StickerPreviewAnimationMode.OnMouseMoveReset:
        svgContainer.addEventListener('touchstart', onTouchStart);
        svgContainer.addEventListener('touchend', onTouchEnd);
        svgContainer.addEventListener('touchmove', onTouchMove);
        svgContainer.addEventListener('mousemove', onFollowMousePosition);
        svgContainer.addEventListener('mouseenter', onMouseEnter);
        svgContainer.addEventListener('mouseleave', onMouseLeave);
        window.addEventListener('scroll', onScroll);
        return () => {
          svgContainer.removeEventListener('mousemove', onFollowMousePosition);
          svgContainer.removeEventListener('mouseenter', onMouseEnter);
          svgContainer.removeEventListener('mouseleave', onMouseLeave);
          window.removeEventListener('scroll', onScroll);
        };
      case StickerPreviewAnimationMode.OnDeviceOrientation:
        addEventListener('deviceorientation', handleOrientation, false);
        return () => removeEventListener('deviceorientation', handleOrientation, false);
    }
  });

  function handleOrientation(event: DeviceOrientationEvent) {
    let rect = svgContainer?.getBoundingClientRect();
    if (rect) {
      let x = event.beta; // In degree in the range [-180,180)
      let y = event.gamma; // In degree in the range [-90,90)

      if (x && y) {
        // Because we don't want to have the device upside down
        // We constrain the x value to the range [-90,90]
        if (x > 40) {
          x = 40;
        }
        if (x < -40) {
          x = -40;
        }

        // To make computation easier we shift the range of
        // x and y to [0,180]
        x += 80;
        y += 80;

        // Reversed the axis because of protrait mode
        let gammaFactor = y / 180;
        let betaFactor = x / 180;
        let xPos: number = Math.round(gammaFactor * rect.width + rect.left);
        let yPos: number = Math.round(betaFactor * rect.height + rect.top);
        // let x = (currentNumber/100) * rect.width + rect.left;
        // let y = (currentNumber/100) * rect.height + rect.top;
        renderMouseMove(xPos, yPos, rect);
      }
    }
  }

  async function loopAnimation(duration: number): Promise<void> {
    await animateMouseMovement(
      { startPosition: 0, endPosition: 100 },
      { startPosition: 0, endPosition: 100 },
      duration,
    );
    await animateMouseMovement(
      { startPosition: 100, endPosition: 100 },
      { startPosition: 100, endPosition: 0 },
      duration,
    );
    await animateMouseMovement(
      { startPosition: 100, endPosition: 100 },
      { startPosition: 0, endPosition: 100 },
      duration,
    );
    await animateMouseMovement(
      { startPosition: 100, endPosition: 0 },
      { startPosition: 100, endPosition: 0 },
      duration,
    );
    await animateMouseMovement(
      { startPosition: 0, endPosition: 50 },
      { startPosition: 0, endPosition: 50 },
      duration / 2,
    );
    await animateMouseMovement(
      { startPosition: 50, endPosition: 100 },
      { startPosition: 50, endPosition: 50 },
      duration / 2,
    );
    await animateMouseMovement(
      { startPosition: 100, endPosition: 0 },
      { startPosition: 50, endPosition: 50 },
      duration / 2,
    );
    await animateMouseMovement(
      { startPosition: 0, endPosition: 50 },
      { startPosition: 50, endPosition: 50 },
      duration / 2,
    );
    await animateMouseMovement(
      { startPosition: 50, endPosition: 50 },
      { startPosition: 50, endPosition: 100 },
      duration / 2,
    );
    await animateMouseMovement(
      { startPosition: 50, endPosition: 0 },
      { startPosition: 100, endPosition: 0 },
      duration / 2,
    );

    return new Promise((resolve) => {
      loopAnimation(duration);
      resolve();
    });
  }

  let animationLoopRef: number;
  function animateMouseMovement(
    x: { startPosition: number; endPosition: number },
    y: { startPosition: number; endPosition: number },
    duration: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      if (!svgContainer) {
        resolve();
        return;
      }

      const startTime = performance.now();
      let rect = svgContainer?.getBoundingClientRect();

      function easeInOutQuad(t: number): number {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      }

      function animateRender(currentTime: number) {
        if (settings.showFps) {
          fpsCount.update();
        }
        const elapsedTime = currentTime - startTime;
        const rawProgress = Math.min(elapsedTime / duration, 1);
        // Apply the ease-out function
        const easedProgress = easeInOutQuad(rawProgress);

        let currentFactorX: number =
          x.startPosition + (x.endPosition - x.startPosition) * easedProgress;
        let currentFactorY: number =
          y.startPosition + (y.endPosition - y.startPosition) * easedProgress;
        let xPos: number = (currentFactorX / 100) * rect.width + rect.left;
        let yPos: number = (currentFactorY / 100) * rect.height + rect.top;
        // let x = (currentNumber/100) * rect.width + rect.left;
        // let y = (currentNumber/100) * rect.height + rect.top;
        renderMouseMove(xPos, yPos, rect);

        if (rawProgress < 1) {
          animationLoopRef = requestAnimationFrame(animateRender); // Fortsätter animera
        } else {
          resolve();
        }
      }

      animationLoopRef = requestAnimationFrame(animateRender);
    });
  }

  function renderMouseMove(x: number, y: number, rect: DOMRect): void {
    // get the mouse position within the domRoot element
    posX = x - rect.left;
    posY = y - rect.top;
    //updating relative pointer position
    relX = -(posX / svgContainer.clientWidth - 0.5).toFixed(3);
    relY = +(posY / svgContainer.clientHeight - 0.5).toFixed(3);

    // getting angle value from pointer position and screen center
    const angle = Math.atan2(
      posY - baseProps.sticker.height / 2,
      posX - baseProps.sticker.width / 2,
    );
    angleDeg = (angle * 180) / Math.PI + 90;
    if (angleDeg < 0) {
      angleDeg = angleDeg + 360;
    }
    //updating CSS variables
    svgCanvas?.style.setProperty('--posX', (-relX).toString());
    svgCanvas?.style.setProperty('--posY', (-relY).toString());
    adjustShadow();

    if (
      baseProps.material.type === MaterialName.Glitter ||
      baseProps.material.type === MaterialName.Pixie
    ) {
      moveGlitter();
    }

    if (baseProps.material.type == MaterialName.Reflective) {
      moveReflective();
    }
  }

  async function runResetAnimation(): Promise<void> {
    if (animationRunning) {
      return;
    }
    animationRunning = true;
    return new Promise((resolve) => {
      const animationspeed = 10;
      let lastFrameTime = performance.now();

      function animateRender(currentTime: number) {
        if (abortAnimation == true) {
          abortAnimation = false;
          currentMousePosition = centralizePosition(currentMousePosition);
          resolve();
          animationRunning = false;
          return;
        }

        const elapsedTimeSeconds = (currentTime - lastFrameTime) * 0.001;
        lastFrameTime = currentTime;

        let velocityX =
          (targetMousePosition.x - currentMousePosition.x) * elapsedTimeSeconds * animationspeed;
        let velocityY =
          (targetMousePosition.y - currentMousePosition.y) * elapsedTimeSeconds * animationspeed;

        currentMousePosition.x += velocityX;
        currentMousePosition.y += velocityY;

        const toTargetX = targetMousePosition.x - currentMousePosition.x;
        const toTargetY = targetMousePosition.y - currentMousePosition.y;

        if (toTargetX < 0 && currentMousePosition.x < targetMousePosition.x) {
          currentMousePosition.x = targetMousePosition.x;
        }
        if (toTargetX > 0 && currentMousePosition.x > targetMousePosition.x) {
          currentMousePosition.x = targetMousePosition.x;
        }

        if (toTargetY < 0 && currentMousePosition.y < targetMousePosition.y) {
          currentMousePosition.y = targetMousePosition.y;
        }
        if (toTargetY > 0 && currentMousePosition.y > targetMousePosition.y) {
          currentMousePosition.y = targetMousePosition.y;
        }

        if (Math.abs(toTargetX) < 10) {
          targetMousePosition.x = currentMousePosition.x;
        }
        if (Math.abs(toTargetY) < 10) {
          targetMousePosition.y = currentMousePosition.y;
        }

        renderMouseMove(
          currentMousePosition.x,
          currentMousePosition.y,
          svgContainer.getBoundingClientRect(),
        );

        if (
          currentMousePosition.x == targetMousePosition.x &&
          currentMousePosition.y == targetMousePosition.y
        ) {
          resolve();
          animationRunning = false;
          return;
        }

        requestAnimationFrame(animateRender);
      }

      requestAnimationFrame(animateRender);
    });
  }

  function centralizePosition(pos: MousePosition): MousePosition {
    if (!svgContainer) {
      return pos;
    }
    const rect = svgContainer.getBoundingClientRect();
    return {
      x: rect.left + rect.width * 0.5,
      y: rect.top + rect.height * 0.5,
    };
  }

  function onMouseLeave() {
    targetMousePosition = centralizePosition(targetMousePosition);
  }

  function onMouseEnter(e: MouseEvent) {
    targetMousePosition.x = e.clientX;
    targetMousePosition.y = e.clientY;
    currentMousePosition = centralizePosition(currentMousePosition);
  }

  function onTouchStart(e: TouchEvent) {
    performTouchMove(e);
    e.preventDefault();
    e.stopImmediatePropagation();
    runResetAnimation();
  }

  function onTouchEnd(e: TouchEvent) {
    targetMousePosition = centralizePosition(targetMousePosition);
    e.preventDefault();
    e.stopImmediatePropagation();
    runResetAnimation();
  }

  function onTouchMove(e: TouchEvent) {
    performTouchMove(e);
    e.preventDefault();
    e.stopImmediatePropagation();
    runResetAnimation();
  }

  function performTouchMove(e: TouchEvent) {
    const x = e.touches[0].clientX;
    const y = e.touches[0].clientY;
    const rect = svgContainer.getBoundingClientRect();
    const inside = x >= rect.left && x <= rect.width && y >= rect.top && y <= rect.height;

    if (inside) {
      targetMousePosition.x = x;
      targetMousePosition.y = y;
    } else {
      targetMousePosition = centralizePosition(targetMousePosition);
    }
  }

  function onFollowMousePosition(e: MouseEvent) {
    if (settings.showFps) {
      fpsCount.update();
    }
    targetMousePosition.x = e.clientX;
    targetMousePosition.y = e.clientY;
    runResetAnimation();
  }

  function onScroll() {
    abortAnimation = true;
  }

  $effect(() => {
    if (!wiggle) {
      if (svgContainer) {
        const rect = svgContainer?.getBoundingClientRect();
        renderMouseMove(rect.width * 0.5, rect.height * 0.5, rect);
      }
    }
  });

  let isAnimating = false;
  function onMouseMovement(e: MouseEvent) {
    if (settings.showFps) {
      fpsCount.update();
    }

    e.stopImmediatePropagation();
    if (wiggle && !isAnimating && svgContainer) {
      requestAnimationFrame(() => {
        if (svgContainer) {
          isAnimating = true;
          renderMouseMove(e.clientX, e.clientY, svgContainer.getBoundingClientRect());
          isAnimating = false;
        }
      });
    }
  }

  function moveGlitter() {
    if (!glitterSheetData || glitterSheetData.indices.length === 0) {
      return;
    }

    const angleChange = relY * 120 + relX * 240;
    let totalPositions: number = glitterSheetData.indices.length;
    let glitterPositionIndex = Math.abs(Math.floor(angleChange / 5)) % totalPositions;

    let glitterPosition = glitterSheetData.indices[glitterPositionIndex];

    if (glitterPosition === undefined) {
      return;
    }
    glitterMaskSprite.style.transform = `translate(-${glitterPosition.x * glitterSheetData.patternScale}px, -${glitterPosition.y * glitterSheetData.patternScale}px)`;
  }

  function moveReflective() {
    /* This is perfect. Glitter and pixel should use this too.
    Refactored function params: totalframes. Speed modifier (5 in anglechange)
    number of x-frames and y-frames
    */
    const totalFrames = 4 * 3 - 1; // 11 total frames (0-based index)
    const angleChange = Math.abs(Math.floor((relY * 120 + relX * 240) / 10)) % (totalFrames * 2);
    /*     console.log(angleChange);
     */
    let framePosition = angleChange % (totalFrames * 2); // Loop from 0 to 11, then back to 0

    if (framePosition > totalFrames) {
      // Reverse motion: count backwards
      framePosition = totalFrames * 2 - framePosition;
    }

    frameIndexX = framePosition % 4; // Move along X-axis (0 → 3 or 3 → 0)
    frameIndexY = Math.floor(framePosition / 4) % 3; // Move along Y-axis (switch after 4 X-frames)
  }

  function adjustShadow() {
    if (!svgCanvas) {
      return;
    }

    // Get the bounding box of the SVG
    const bbox = svgCanvas.getBoundingClientRect();
    const maxSize = Math.max(bbox.width, bbox.height);

    // Scale the shadow offset based on SVG size
    const baseSize = 500; // Reference SVG size for full offset effect
    const scaledOffset = (shadowOffset * maxSize) / baseSize; // Scale it relative to the SVG size
    const scaledBlur = maxSize * 0.02; // Blur is 5% of the SVG size

    // Ensure the offset doesn't get too small or too large
    const minOffset = 5;
    const maxOffset = 30;
    const finalOffset = Math.min(maxOffset, Math.max(minOffset, scaledOffset));

    let shadowValues = `${(-relX * finalOffset * 2).toFixed(1)}px ${(relY * finalOffset).toFixed(
      1,
    )}px ${scaledBlur}px rgba(0,0,30,0.3)`;

    svgCanvas.style.setProperty('--shadow', shadowValues);
  }

  $effect(() => {
    // Reset masks immediately to prevent flash
    whitelayerMaskString = '';
    opacityMaskString = '';

    if (baseProps.sticker.whiteLayerMasks.length > 0) {
      if (maskSource === MaskSource.COMBINED) {
        // Synchronous assignment - no flashing
        whitelayerMaskString =
          baseProps.sticker.whiteLayerMasks.find((mask) => mask.mode === WhiteLayerMaskMode.Color)
            ?.maskImgUrl || '';
        opacityMaskString =
          baseProps.sticker.whiteLayerMasks.find((mask) => mask.mode === WhiteLayerMaskMode.Effect)
            ?.maskImgUrl || '';
      } else {
        // Use a local flag to prevent race conditions
        let isCurrent = true;

        generateMasks(baseProps).then((result) => {
          // Only update if this effect is still current
          if (isCurrent) {
            whitelayerMaskString = result.b64WhiteLayerMask;
            opacityMaskString = result.b64OpacityMask;
          }
        });

        // Cleanup function to mark this effect as stale
        return () => {
          isCurrent = false;
        };
      }
    }
  });

  const holographicImageUrl = $derived.by(() => {
    switch (settings.holoIndex) {
      case 0:
        return staticUrl + '/images/web/preview/effects/holographic-swirl.png';
      case 1:
        return staticUrl + '/images/web/preview/effects/holographic-swirl2.png';
      case 2:
        return staticUrl + '/images/web/preview/effects/holoimage.png';
      case 3:
        return staticUrl + '/images/web/preview/effects/holoimage_blur.png';
      case 4:
        return staticUrl + '/images/web/preview/effects/holoimage_2.png';
      case 5:
        return staticUrl + '/images/web/preview/effects/holoimage_3.png';
    }
  });

  let isAllMasksColor: boolean = $derived.by(() => {
    return (
      baseProps.sticker.whiteLayerMasks.length > 0 &&
      maskSource === MaskSource.GENERATED &&
      baseProps.sticker.whiteLayerMasks.every((mask) => mask.mode === WhiteLayerMaskMode.Color)
    );
  });

  let printLayerStyle = $derived.by(() => {
    let opacity = '';
    let blendMode = 'mix-blend-mode: multiply;';

    switch (baseProps.material.type) {
      case MaterialName.Recycled:
      case MaterialName.KraftThin:
        opacity = 'opacity: 0.9;';
        break;
      case MaterialName.Pixie:
      case MaterialName.Glitter:
        blendMode = 'mix-blend-mode: color-burn;';
        break;
      default:
        break;
    }

    if (!useWhitelayer) {
      blendMode = 'mix-blend-mode: normal;';
    }

    return [opacity, blendMode].filter(Boolean).join(' ');
  });
  let basePlateLayerStyle = $derived.by(() => {
    let fill = `fill: ${getFillForMaterial(baseProps.material.type)};`;
    let stroke = '';
    let strokeWidth = '';
    switch (baseProps.material.type) {
      case MaterialName.Clear:
      case MaterialName.ClearThin:
        stroke = 'stroke: #a3a3a385;';
        strokeWidth = 'stroke-width: 1px;';
        break;
      default:
        break;
    }
    return [fill, stroke, strokeWidth].filter(Boolean).join(' ');
  });

  const useWhitelayer = $derived.by(() => {
    if (maskSource === MaskSource.UPLOADED) {
      return true;
    }
    if (maskSource === MaskSource.GENERATED) {
      return !isAllMasksColor;
    }
    return !baseProps.useImageAlphaAsWhitelayer;
  });
</script>

{#if settings.showFps}
  <div
    style={`
    position:absolute;
    font-weight: bold;
    font-size: 20px;
    color:crimson;
    z-index: 100;
    top: ${650}px;
    left: 280px;
    `}
  >
    {'FPS: ' + fpsCount.getFps()}
  </div>
{/if}

{#snippet previewDefs()}
  {#if baseProps.material.type == MaterialName.Prismatic}
    {@render previewPrismaticPattern()}
  {/if}
  {#if baseProps.material.type == MaterialName.Holographic}
    {@render previewHolographicImage()}
  {/if}

  {#if baseProps.material.type == MaterialName.Glitter || baseProps.material.type == MaterialName.Pixie}
    {@render previewGlitterMask()}
  {/if}
  {#if baseProps.material.type == MaterialName.Brushed}
    {@render previewBrushed()}
  {/if}
  {#if baseProps.material.type == MaterialName.Reflective}
    {@render reflectiveMaterial()}
  {/if}

  {@render previewPrintImage()}
  {@render previewClipPath()}
  {#if !settings.lightweight}
    {@render previewTurbulenceFilter()}
  {/if}
  {#if wiggle}
    {@render previewLaminateImage()}
  {/if}
  {#if wiggle || baseProps.material.type == MaterialName.Mirror}
    {@render previewMetallicImage()}
  {/if}

  {#if baseProps.material.type == MaterialName.Recycled || baseProps.material.type == MaterialName.KraftThin}
    {@render previewRecycledPattern()}
  {/if}
  {@render previewBasePlate()}
  {@render invertFilter()}
{/snippet}

{#snippet invertFilter()}
  <filter id="invertFilter-{uniqueId}">
    <feComponentTransfer>
      <feFuncR type="table" tableValues="1 0" />
      <feFuncG type="table" tableValues="1 0" />
      <feFuncB type="table" tableValues="1 0" />
    </feComponentTransfer>
  </filter>
{/snippet}

{#snippet previewBrushed()}
  <pattern
    id="brushed-pattern-{uniqueId}"
    patternUnits="userSpaceOnUse"
    width="300"
    height="300"
    patternTransform="scale({settings.patternScale})"
  >
    <image
      width="300"
      height="300"
      preserveAspectRatio="xMidYMid meet"
      href={staticUrl + '/images/web/preview/effects/brush-tile.jpg'}
      transform="rotate(90 150 150)"
    />
  </pattern>
  <rect
    id="brushedLayerDef-{uniqueId}"
    fill="url(#brushed-pattern-{uniqueId})"
    width={fullWidth}
    height={fullHeight}
  />
{/snippet}

{#snippet previewBasePlate()}
  <path id="baseplate-{uniqueId}" d={baseProps.sticker.path}></path>
{/snippet}

{#snippet previewClipPath()}
  <clipPath id="clippy-{uniqueId}" class="clip-path">
    <path id="clippySvgPath-{uniqueId}" d={baseProps.sticker.path}></path>
  </clipPath>
{/snippet}

{#snippet previewHolographicImage()}
  <image
    style="transform: translate({-relX * (holographicImageSize.width / 1.5)}px, {relY *
      (holographicImageSize.height / 1.5)}px);"
    id="holographicLayerDef-{uniqueId}"
    href={holographicImageUrl}
    width={holographicImageSize.width}
    height={holographicImageSize.height}
  />
{/snippet}

{#snippet previewRecycledPattern()}
  <pattern
    id="recycled-pattern-{uniqueId}"
    patternUnits="userSpaceOnUse"
    width={recycledPatternSize}
    height={recycledPatternSize}
    patternTransform="scale({settings.patternScale})"
  >
    <image
      width={recycledPatternSize}
      height={recycledPatternSize}
      href={staticUrl + '/images/web/preview/effects/recycled.jpg'}
      preserveAspectRatio="xMinYMid meet"
    />
  </pattern>
  <rect
    id="recycledLayerDef-{uniqueId}"
    fill="url(#recycled-pattern-{uniqueId})"
    width={fullWidth}
    height={fullHeight}
  />
{/snippet}

{#snippet previewPrismaticPattern()}
  {@const angleChange = relY * 360 + relX * 360}
  <pattern
    id="prism-pattern-{uniqueId}"
    patternUnits="userSpaceOnUse"
    width="40"
    height="40"
    patternTransform="scale({settings.patternScale})"
  >
    <image
      id="prism-img-{uniqueId}"
      width="80"
      height="80"
      transform-origin="20 20"
      href={staticUrl + '/images/web/preview/effects/prism-100x100.png'}
      transform="rotate({angleChange}) translate(-20 -20)"
      opacity={Math.min(Math.abs(relX + 0.6), 0.6)}
    />

    <image
      id="prism-img-addition-{uniqueId}"
      width="80"
      height="80"
      transform-origin="20 20"
      preserveAspectRatio="xMidYMid slice"
      href={staticUrl + '/images/web/preview/effects/prism-addition-100x100_1.png'}
      transform="rotate({angleChange}) translate(-20 -20)"
      opacity={Math.abs(relX)}
    />
  </pattern>
  <rect
    id="prismaticLayerDef-{uniqueId}"
    fill="url(#prism-pattern-{uniqueId})"
    height={fullHeight}
    width={fullWidth}
    style="filter: hue-rotate({angleChange}deg);"
  />
{/snippet}

{#snippet previewTurbulenceFilter()}
  <filter id="displacementFilter-{uniqueId}" filterUnits="objectBoundingBox">
    <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="1" result="turbulence" />
    <feDisplacementMap
      in2="turbulence"
      in="SourceGraphic"
      scale="20"
      xChannelSelector="R"
      yChannelSelector="G"
    />
  </filter>
{/snippet}

{#snippet previewLaminateImage()}
  <image
    bind:this={laminateImage}
    width={laminateAndMetallicImageSize.width}
    height={laminateAndMetallicImageSize.height}
    id="laminateLayerDef-{uniqueId}"
    style="transform: translate({wiggle
      ? -relX * 200 * laminateAndMetallicImageSize.scale
      : 0}px, {wiggle ? relY * 200 * laminateAndMetallicImageSize.scale : 0}px);"
    scale={1}
    href={isShineLaminate
      ? staticUrl + '/images/web/preview/reflections/escaltor-shine.jpg'
      : staticUrl + '/images/web/preview/reflections/escaltor-matte.jpg'}
    preserveAspectRatio="xMidYMid slice"
    transform-origin="center"
  />
{/snippet}

{#snippet previewMetallicImage()}
  <image
    id="metallicLayerDef-{uniqueId}"
    opacity={metallicOpacity}
    href={isShineLaminate
      ? staticUrl + '/images/web/preview/reflections/escaltor-shine.jpg'
      : staticUrl + '/images/web/preview/reflections/escaltor-matte.jpg'}
    width={laminateAndMetallicImageSize.width}
    height={laminateAndMetallicImageSize.height}
    scale={1}
    style="transform: translate({wiggle
      ? -relX * 200 * laminateAndMetallicImageSize.scale
      : 0}px, {wiggle ? relY * 200 * laminateAndMetallicImageSize.scale : 0}px);"
    preserveAspectRatio="xMidYMid slice"
    transform-origin="center"
  />
{/snippet}

{#snippet previewPrintImage()}
  <g id="imageGroup-{uniqueId}">
    <!-- whitelayer rect -->
    {#if useWhitelayer}
      <rect
        id="whitelayer-{uniqueId}"
        width="100%"
        height="100%"
        fill="white"
        style="mask: url(#whitelayer-mask-{uniqueId});"
      />
    {/if}
    <image
      href={baseProps.sticker.previewImage}
      bind:this={printImage}
      width={baseProps.sticker.width}
      height={baseProps.sticker.height}
      id="printImage-{uniqueId}"
      mask="url(#opacity-mask-{uniqueId})"
    />
  </g>
{/snippet}

{#snippet previewGlitterMask()}
  <pattern
    id="glitterMaskPattern-{uniqueId}"
    patternUnits="userSpaceOnUse"
    width={glitterSheetData.frameWidth * glitterSheetData.patternScale}
    height={glitterSheetData.frameHeight * glitterSheetData.patternScale}
    patternTransform="scale(1)"
  >
    <image
      bind:this={glitterMaskSprite}
      width={glitterSheetData.width * glitterSheetData.patternScale}
      height={glitterSheetData.height * glitterSheetData.patternScale}
      href={staticUrl + glitterSheetData.imgSrc}
      preserveAspectRatio="xMinYMid meet"
    ></image>
  </pattern>

  <mask id="glitterMask-{uniqueId}">
    <rect width="100%" height="100%" fill="url(#glitterMaskPattern-{uniqueId})"></rect>
  </mask>

  <image
    style="transform: translate({-relX * (holographicImageSize.width / 1.5)}px, {relY *
      (holographicImageSize.height / 1.5)}px);"
    id="glitterLayerDef-{uniqueId}"
    href={staticUrl + '/images/web/preview/effects/holoimage_3.png'}
    width={holographicImageSize.width}
    height={holographicImageSize.height}
  />
{/snippet}
{#snippet reflectiveMaterial()}
  <!-- Define the pattern -->
  <pattern
    id="reflectivePattern-{uniqueId}"
    patternUnits="userSpaceOnUse"
    width={(1080 * settings.patternScale) / 13}
    height={(1080 * settings.patternScale) / 13}
  >
    <image
      id="reflectiveImage-{uniqueId}"
      href={staticUrl + '/images/web/preview/effects/reflex_sprite.jpg'}
      width={(4320 * settings.patternScale) / 13}
      height={(3240 * settings.patternScale) / 13}
      x="-{(frameIndexX * 1080 * settings.patternScale) / 13}"
      y="-{(frameIndexY * 1080 * settings.patternScale) / 13}"
      preserveAspectRatio="none"
      data-indexX={frameIndexX}
      data-indexY={frameIndexY}
    />
  </pattern>

  {#if settings.lightweight}
    <!-- Simplified version for Safari -->
    <rect
      id="reflectiveLayerDef-{uniqueId}"
      fill="url(#reflectivePattern-{uniqueId})"
      width={fullWidth}
      height={fullHeight}
      opacity={0.7}
      style="mix-blend-mode: overlay;"
    />
  {:else}
    <mask id="reflectiveMask-{uniqueId}">
      <rect width="100%" height="100%" fill="url(#reflectivePattern-{uniqueId})"></rect>
    </mask>
    <rect
      id="reflectiveLayerDef-{uniqueId}"
      fill={MaskFillColor.Color}
      width={fullWidth}
      height={fullHeight}
      style="mask: url(#reflectiveMask-{uniqueId});"
    />
  {/if}
{/snippet}
<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
  id="svgContainer-{uniqueId}"
  bind:this={svgContainer}
  class={clsx('svg-container overflow-hidden', baseProps.material.type, className)}
  class:inspecting={wiggle}
>
  {#if visible}
    <StickerPreviewRulers
      show={!wiggle || (settings.showMeasures ?? false)}
      {uniqueId}
      {productItemDimensions}
      backgroundOption={settings.backgroundOption}
    />
    <svg
      in:slide={{
        duration: settings.lightweight ? 0 : 200,
        delay: settings.lightweight ? 0 : 300,
      }}
      bind:this={svgCanvas}
      class={clsx(sizeClasses, 'svgCanvas h-full w-full')}
      color-interpolation-filters="linearRGB"
      id="canvas-{uniqueId}"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      viewBox={`0 0 ${fullWidth} ${fullHeight}`}
      transform="scale(.8)"
    >
      <defs>
        {#key whitelayerMaskString || opacityMaskString}
          {#if settings.effectLayerEnabled}
            <g bind:this={whiteLayerGroup}>
              <mask id="inverted-whitelayer-mask-{uniqueId}">
                {#if whitelayerMaskString !== '' && !isLoading && useWhitelayer}
                  <image
                    height={fullHeight}
                    width={fullWidth}
                    href={whitelayerMaskString}
                    filter="url(#invertFilter-{uniqueId})"
                  />
                {:else}
                  <rect width={fullWidth} height={fullHeight} fill={MaskFillColor.Color} />
                {/if}
              </mask>
              {#if useWhitelayer}
                <mask id="whitelayer-mask-{uniqueId}">
                  {#if whitelayerMaskString !== '' && !isLoading}
                    <image
                      id="whitelayerMask-{uniqueId}"
                      height={fullHeight}
                      width={fullWidth}
                      href={whitelayerMaskString}
                    />
                  {/if}
                </mask>
              {/if}
              <mask id="opacity-mask-{uniqueId}">
                {#if !isLoading && useWhitelayer}
                  {#if baseProps.material.type === MaterialName.Clear || settings.adminPreview}
                    <image height={fullHeight} width={fullWidth} href={whitelayerMaskString} />
                  {/if}
                  {#if opacityMaskString !== ''}
                    <image
                      height={fullHeight}
                      width={fullWidth}
                      href={opacityMaskString}
                      style="opacity: {baseProps.material.type === MaterialName.Clear ||
                      settings.adminPreview
                        ? 0.5
                        : 1}"
                    />
                  {:else}
                    <rect
                      width={fullWidth}
                      height={fullHeight}
                      fill={MaskFillColor.Color}
                      style="opacity: {baseProps.material.type === MaterialName.Clear ||
                      settings.adminPreview
                        ? 0.5
                        : 1}"
                    />
                  {/if}
                {:else}
                  <rect width={fullWidth} height={fullHeight} fill={MaskFillColor.Color} />
                {/if}
              </mask>
            </g>
          {/if}
        {/key}
        {@render previewDefs()}
      </defs>
      {#key baseProps}
        <StickerPreviewLayers
          selectedMaterial={baseProps.material}
          bind:settings
          {uniqueId}
          bind:printLayerStyle
          bind:basePlateLayerStyle
        />
      {/key}
    </svg>
  {/if}
</div>

<style>
  svg {
    &.max-width-70 {
      max-width: 70%;
    }
    &.max-height-70 {
      max-height: 70%;
    }
    &.lightweight-optimizations {
      max-width: 60%;
      max-height: 60%;
      image-rendering: optimizeSpeed;
      shape-rendering: optimizeSpeed;
    }
  }
  .clip-path path {
    fill: #aaa;
  }
  svg {
    filter: drop-shadow(var(--shadow));
    --posX: 0;
    --posY: 0;
    --accentColor: #cfb400;
    --shadow: 0 0 15px rgba(0, 0, 30, 0.12);
    --hueshift: 0;
  }
  .svg-container.inspecting .svgCanvas {
    /*Scaling to maximum 1 for better quality image.
    Newsalert Scaling sometimes creates tearing at the edges of the svg.
    If this is a big problem, scale 1 to 1.2 instead*/
    transform: perspective(600px) rotateX(calc(var(--posY) * -45deg))
      rotateY(calc(var(--posX) * -45deg));
  }

  .svg-container .svgCanvas {
    transform: scale(1) translate3d(0, 0, 0);
  }

  .svg-container {
    display: grid;
    place-items: center;
    width: 100%; /* Adjust to your desired width */
    height: 100%; /* Adjust to your desired height */
    /* position relative for positioning background options in container */
    position: relative;
  }
</style>
